import { useCallback, useEffect, useMemo, useState } from 'react';
import { Image as ImageIcon } from 'lucide-react';
import ImagesUploader from '../Questionnaire/ImagesUploader';
import { FileProvider } from '../../contexts/FileContext';
import { fileService, moldImageService, MoldImage } from '../../services/api';
import { FileResponse } from '../../types/moldOrder';
import { FileInfo } from '../../types/upload';
import Modal from '../shared/Modal';
import Portal from '../shared/Portal';
import FashionGallery from '../Questionnaire/FashionGallery';

interface MoldImagesProps {
  moldId: string;
}
interface MoldImageTileProps extends MoldImagesProps {
  variant?: 'compact' | 'cover';
}

const mutationErrorMessage = (error: unknown, fallback: string) => {
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  if (status === 403) return 'ไม่มีสิทธิ์แก้ไขรูปภาพแม่พิมพ์';
  if (status === 409)
    return 'ข้อมูลรูปภาพมีการเปลี่ยนแปลง กรุณาโหลดใหม่แล้วลองอีกครั้ง';
  return fallback;
};

const useMoldImages = (moldId: string) => {
  const [files, setFiles] = useState<FileResponse[]>([]);
  const [images, setImages] = useState<MoldImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const refreshFiles = useCallback(async () => {
    const fileResult = await fileService.getFiles('mold', Number(moldId));
    setFiles(fileResult.data || []);
  }, [moldId]);
  const reload = useCallback(async () => {
    if (!Number(moldId)) return;
    setError(false);
    try {
      const [fileResult, imageResult] = await Promise.all([
        fileService.getFiles('mold', Number(moldId)),
        moldImageService.list(moldId),
      ]);
      setFiles(fileResult.data || []);
      const records = imageResult.data || [];
      setImages(records);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [moldId]);
  useEffect(() => {
    const load = async () => reload();
    void load();
  }, [reload]);
  const orderedFiles = useMemo(() => {
    const byName = new Map(files.map((file) => [file.fileName, file]));
    const result: FileResponse[] = [];
    images.forEach((image) => {
      const file = byName.get(image.fileName);
      if (file) result.push({ ...file, notes: image.caption || '' });
    });
    return result;
  }, [files, images]);
  return {
    images,
    orderedFiles,
    loading,
    error,
    reload,
    refreshFiles,
    setImages,
  };
};

/** Compact cover tile; the same gallery viewer is used by the image section. */
export const MoldImageTile = ({
  moldId,
  variant = 'compact',
}: MoldImageTileProps) => {
  const [images, setImages] = useState<MoldImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [viewerImages, setViewerImages] = useState<string[]>([]);
  const [viewerNotes, setViewerNotes] = useState<string[]>([]);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let alive = true;
    let coverUrl: string | null = null;
    moldImageService
      .list(moldId)
      .then(async ({ data }) => {
        const records = data || [];
        if (!alive) return;
        setImages(records);
        const cover = records.find((image) => image.isCover) || records[0];
        if (!cover) return;
        try {
          const response = await fileService.downloadFile(cover.fileName);
          coverUrl = URL.createObjectURL(response.data);
          if (alive) setPreviewUrl(coverUrl);
        } catch {
          /* The tile falls back to the image icon. */
        }
      })
      .catch(() => {
        // Keep the header usable when gallery metadata is temporarily unavailable.
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
      if (coverUrl) URL.revokeObjectURL?.(coverUrl);
    };
  }, [moldId]);
  useEffect(
    () => () => {
      viewerImages.forEach((url) => URL.revokeObjectURL?.(url));
    },
    [viewerImages],
  );
  const openViewer = async () => {
    if (!images.length) return;
    if (!viewerImages.length) {
      const loaded = await Promise.all(
        images.map(async (image) => {
          try {
            const response = await fileService.downloadFile(image.fileName);
            return {
              url: URL.createObjectURL(response.data),
              caption: image.caption || '',
              isCover: image.isCover,
            };
          } catch {
            return null;
          }
        }),
      );
      const successful = loaded.filter(
        (image): image is { url: string; caption: string; isCover: boolean } =>
          image !== null,
      );
      if (!successful.length) return;
      const nextViewerIndex = Math.max(
        0,
        successful.findIndex((image) => image.isCover),
      );
      setViewerImages(successful.map((image) => image.url));
      setViewerNotes(successful.map((image) => image.caption));
      setViewerIndex(nextViewerIndex);
    }
    setOpen(true);
  };
  const body = previewUrl ? (
    <img
      src={previewUrl}
      alt='ภาพหน้าปกแม่พิมพ์'
      className='h-full w-full object-cover'
    />
  ) : (
    <ImageIcon size={22} aria-hidden='true' />
  );
  const buttonClass =
    variant === 'cover'
      ? 'group relative ml-5 h-14 w-14 shrink-0 overflow-hidden rounded-lg border border-gray-200 bg-gray-50 text-gray-400 shadow-sm transition hover:border-blue-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500'
      : 'group flex w-full items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 p-2 text-left text-gray-400 transition hover:border-blue-300 hover:bg-blue-50';
  return (
    <>
      <button
        type='button'
        onClick={openViewer}
        disabled={!images.length}
        aria-label='เปิดรูปภาพแม่พิมพ์'
        className={buttonClass}
      >
        {variant === 'cover' ? (
          <>
            <span className='block h-full w-full'>{body}</span>
            {!loading && images.length > 0 && (
              <span className='absolute bottom-0 right-0 min-w-4 rounded-tl bg-black/65 px-1 text-center text-[10px] leading-4 text-white'>
                {images.length}
              </span>
            )}
          </>
        ) : (
          <>
            <span className='flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white ring-1 ring-gray-200'>
              {body}
            </span>
            <span>
              <span className='block text-sm font-semibold text-gray-800'>
                รูปภาพแม่พิมพ์
              </span>
              <span className='block text-xs text-gray-500'>
                {loading
                  ? 'กำลังโหลด...'
                  : `${images.length} รูป · เปิดคลังรูป`}
              </span>
            </span>
          </>
        )}
      </button>
      {open && viewerImages.length > 0 && (
        <Portal>
          <Modal
            onClose={() => setOpen(false)}
            maxWidth='max-w-4xl'
            isBackgroundTransparent
          >
            <FashionGallery
              images={viewerImages}
              index={viewerIndex}
              notes={viewerNotes}
            />
          </Modal>
        </Portal>
      )}
    </>
  );
};

const MoldImages = ({ moldId }: MoldImagesProps) => {
  const {
    images,
    orderedFiles,
    loading,
    error,
    reload,
    refreshFiles,
    setImages,
  } = useMoldImages(moldId);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const reorder = async (activeFileName: string, overFileName: string) => {
    const from = images.findIndex((image) => image.fileName === activeFileName);
    const to = images.findIndex((image) => image.fileName === overFileName);
    if (from < 0 || to < 0) return;
    if (from === to) return;
    const next = [...images];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setImages(next.map((image, position) => ({ ...image, position })));
    setMutationError(null);
    try {
      await moldImageService.reorder(
        moldId,
        next.map((image) => image.id),
      );
    } catch (mutationError) {
      setMutationError(
        mutationErrorMessage(
          mutationError,
          'ไม่สามารถบันทึกลำดับรูปภาพได้ กรุณาลองอีกครั้ง',
        ),
      );
      await reload();
    }
  };
  const sync = async (current: FileInfo[]) => {
    setMutationError(null);
    try {
      const recordNames = new Set(images.map((image) => image.fileName));
      const missing = current
        .map((file) => file.fileName)
        .filter((name) => !recordNames.has(name));
      if (missing.length) {
        const records =
          (await moldImageService.sync(moldId, missing)).data || [];
        setImages(records);
        await refreshFiles();
        return;
      }
      await reload();
    } catch (mutationError) {
      setMutationError(
        mutationErrorMessage(
          mutationError,
          'ไม่สามารถบันทึกรูปภาพเข้าคลังได้ กรุณาลองอีกครั้ง',
        ),
      );
      await reload();
    }
  };
  const setCover = async (fileName: string) => {
    const target = images.find((image) => image.fileName === fileName);
    if (!target) return;
    setImages(
      images.map((image) => ({ ...image, isCover: image.id === target.id })),
    );
    setMutationError(null);
    try {
      await moldImageService.update(moldId, target.id, { isCover: true });
    } catch (mutationError) {
      setMutationError(
        mutationErrorMessage(
          mutationError,
          'ไม่สามารถตั้งรูปปกได้ กรุณาลองอีกครั้ง',
        ),
      );
      await reload();
    }
  };
  const saveCaption = async (fileName: string, caption: string) => {
    const target = images.find((image) => image.fileName === fileName);
    if (!target) return;
    setMutationError(null);
    try {
      await moldImageService.update(moldId, target.id, { caption });
      setImages(
        images.map((image) =>
          image.id === target.id ? { ...image, caption } : image,
        ),
      );
    } catch (mutationError) {
      setMutationError(
        mutationErrorMessage(
          mutationError,
          'ไม่สามารถบันทึกคำบรรยายได้ กรุณาลองอีกครั้ง',
        ),
      );
      await reload();
      throw mutationError;
    }
  };
  if (loading)
    return (
      <div className='py-12 text-center text-gray-500'>กำลังโหลดรูปภาพ...</div>
    );
  if (error)
    return (
      <div className='rounded-lg bg-red-50 px-4 py-3 text-red-600'>
        ไม่สามารถโหลดรูปภาพแม่พิมพ์ได้
      </div>
    );
  return (
    <section
      id='mold-images-section'
      aria-labelledby='mold-images-title'
      className='scroll-mt-4'
    >
      <div className='mb-5 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between'>
        <div>
          <h2
            id='mold-images-title'
            className='flex items-center gap-2 text-xl font-bold text-gray-900'
          >
            <ImageIcon size={21} className='text-blue-600' />
            รูปภาพแม่พิมพ์
          </h2>
          <p className='mt-1 text-sm text-gray-500'>
            เพิ่มรูปภาพได้หลายรูป พร้อมคำบรรยาย เช่น ภาพรวม, Name plate, Cavity
            หรือ Core
          </p>
        </div>
        <span className='text-sm font-medium text-gray-500'>
          {orderedFiles.length} รูป
        </span>
      </div>
      {mutationError && (
        <div
          role='alert'
          className='mb-4 rounded-lg bg-red-50 px-4 py-3 text-red-600'
        >
          {mutationError}
        </div>
      )}
      <FileProvider>
        <ImagesUploader
          entityType='mold'
          entityId={Number(moldId)}
          title={`mold-${moldId}-images`}
          displayTitle=''
          hideTag
          files={orderedFiles}
          onFileChange={sync}
          maxPhotoCount={20}
          className='mb-2'
          compactAddControls
          onPhotoReorder={reorder}
          coverFileName={images.find((image) => image.isCover)?.fileName}
          onSetCover={setCover}
          onPhotoCaptionChange={saveCaption}
        />
      </FileProvider>
      {orderedFiles.length === 0 && (
        <p className='mt-4 rounded-lg bg-gray-50 px-4 py-3 text-sm text-gray-500'>
          ยังไม่มีรูปภาพ กด “เพิ่มรูปภาพ” หรือ “ถ่ายรูป”
          เพื่อเพิ่มภาพอ้างอิงของแม่พิมพ์
        </p>
      )}
    </section>
  );
};

export default MoldImages;
